import React from 'react';
import classes from 'classnames';
import { computed, observable, action, runInAction, makeObservable } from 'mobx';
import { observer, inject } from 'mobx-react';

import { DefaultButton, Checkbox, mergeStyleSets, TextField } from '@fluentui/react';

import { TargetPicker } from 'src/components/base/TargetPicker';
import SearchableComboBox from 'src/components/base/SearchableComboBox';
import GroupPane from 'src/components/base/GroupPane';
import { CheckboxTreeListWithSearch, makeId, updateItems, getItem, getItems } from 'src/components/base/TreeList';
import ButtonTooltip from '@v2/components/base/ButtonTooltip';

import { Button } from 'src/components/Flowsteps/base/Flowstep';
import Step from 'src/components/Flowsteps/base/Step';
import PullFlowstep from 'src/components/Flowsteps/PullData/PullFlowstep';


import { Type, generateFlowstepName, isInsertKind } from 'shared/utils/flowstep';
import * as QA from 'shared/utils/queryAnalysis';
import * as QS from 'shared/utils/querySubstitution';
import { runShortAction } from 'src/services/utils';
import { includesParttern } from 'shared/utils/utils';



const classNames = mergeStyleSets({
  codemirror: {
    border: '1px solid #A6A6A6',
    opacity: 0.8,
    margin: '16px 16px 0px 16px',
  },
  block: {
    margin: 16,
  },
  buttonBlock: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'stretch',
    margin: '16px 0px 0px -16px',
    selectors: {
      '& > *': {
        flexGrow: 1,
        marginLeft: 16,
      },
    },
  },
  options: {
    margin: '16px 0px 0px 0px',
  },
  optionsContent: {
    margin: '16px 16px 0px 16px',
    selectors: {
      '& > div': {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '16px',
      },
    },
  },
  optionsContentV: {
    margin: '16px 16px 0px 16px',
    selectors: {
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 14,
      },
    },
  },

  fieldText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    textAlign: 'center',
  },
  fieldTextInactive: {
    color: 'gray',
  },

  contentWithoutGroupPane: {
    margin: '0 16px',
    selectors: {
      '& > div': {
        marginTop: 14,
      }
    }
  },

  withTargetPicker: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'stretch',
  },
});


const Data = {
  Buttons: {
    [Button.Run]: { text: 'Run query', tooltip: 'Executes the specified query without saving it inside of the worksheet as a flow step' },
    [Button.RunAndSave]: { tooltip: 'Executes the specified query and saves it inside of the worksheet as a flow step' },
    [Button.Save]: { tooltip: 'Updates the query in the current flow step without executing it' },
  },
  MaxRelationshipDepth: 5,
  HelpLink: `https://www.xappex.com/docs/enabler4excel365/importing-data-from-salesforce-com-using-soql-salesforce-object-query-language/`,
};


@inject('salesforceStore', 'dataStore', 'officeStore', 'userStore')
export default class Sosl extends PullFlowstep {
  @observable soql = {};

  @observable selectedObjectName = null;
  @observable findExpression = '';
  @observable inExpression = QA.InFieldsType.All;

  @observable sourceFields = [];
  @observable selectedFieldsIds = [];
  @observable expandedFieldsIds = [];
  @observable fieldsFilter = '';

  @observable query = '';
  @observable queryPreview = '';

  constructor(props) {
    super(props, Type.Sosl);
    makeObservable(this);

    this.options = {
      ...this.options,
      queryAll: false,
      showLabels: false,
    };

    const allReactions = [...super.setupBaseReactions()];
    this.cleanReactions = () => allReactions.forEach(dispose => dispose());
  }

  @computed get _generatedName_() {
    return generateFlowstepName({ type: Type.Sosl, sfObjectName: this.selectedObjectName });
  }

  @computed get _isValid_() {
    return super.areAllOptionsValid && this.isValidQuery;
  }

  @computed get _buttonsData_() { return Data.Buttons; }

  @computed get _helpArticleLink_() {
    return Data.HelpLink;
  }

  @computed get objects() {
    const selector = this.options.showLabels ? i => i.label : i => i.name;
    return (this.props ? this.props.officeStore.objects : []).map(i => ({ key: i.name, text: selector(i) }));
  }

  @computed get inTypeOptions() {
    return [
      { key: QA.InFieldsType.All, text: 'All Fields', tooltip: 'Search all searchable fields' },
      { key: QA.InFieldsType.Email, text: 'Email Fields', tooltip: 'Search only email fields' },
      { key: QA.InFieldsType.Name, text: 'Name Fields', tooltip: 'Search only name fields for standard objects. In custom objects, fields that are defined as “Name Field” are searched. In standard and custom objects, name fields have the nameField property set to true.' },
      { key: QA.InFieldsType.Phone, text: 'Phone Fields', tooltip: 'Search only phone number fields' },
      { key: QA.InFieldsType.Sidebar, text: 'Sidebar Fields', tooltip: 'Search for valid records as listed in the Sidebar dropdown list. Unlike search in the application, the asterisk (*) wildcard isn’t appended to the end of a search string' },
    ];
  }

  @computed get fields() {
    const mapFields = (fields) => {
      return fields.map(x => {
        const { fullName, fullLabel, rawField, referencesTo, isPolymorphicChild } = x;
        const { relationshipName, name, label } = rawField;

        const relationshipsTo = relationshipName
          ? ` (${relationshipName} to ${referencesTo.join(', ')})`
          : '';

        const relationship = relationshipName
          ? ` (${isPolymorphicChild ? referencesTo[0] : relationshipName})`
          : '';

        const fullDisplayName = this.options.showLabels ? fullLabel : fullName;
        const tooltip = fullDisplayName + relationshipsTo;
        const title = (this.options.showLabels ? label : name) + relationship;

        return ({ ...x,
          tooltip, title, fullDisplayName,
          descendants: mapFields(x.descendants) });
      });
    }

    return mapFields(this.sourceFields);
  }

  @computed get fakeSubstitutedQuery() {
    return QS.substituteQueryWithFakes(this.query, this.type);
  }

  @computed get isValidQuery() {
    return QA.extractSoslQueryData(this.fakeSubstitutedQuery.query).isValid;
  }

 async _componentDidMountStart_() {
     const task = this.fetchObjects();
     const soqlModule = await import(/* webpackChunkName: "soql" */'../Soql/soqlUtils');
     runInAction(() => {
       this.soql = soqlModule.default || soqlModule;
     });
     return task;
   }

  async _componentDidMountEnd_(task) {
    await task;
  }

  async _loadFlowstep_(flowstep, isExact) {
    console.log('SOSL::_loadFlowstep_: ', flowstep);
    super.loadFlowstep(flowstep, isExact);

    const { showLabels = false, query = '', } = flowstep.options;
    this.options = { ...this.options, showLabels };
    this.query = query;

    const { sourceFields, objectName, usedFields, findExpression, inFieldType } = await this.parseAndLoadQuery(query);

    this.findExpression = findExpression;
    this.inExpression = inFieldType;

    this.selectedObjectName = objectName;
    this.sourceFields = sourceFields;
    this.selectedFieldsIds = usedFields;
  }

  async _makeFlowstep_(makeCopy) {
    const sheetName = this.selectedObjectName || 'Sheet';
    const flowstep = await super.makeFlowstep(sheetName);

    Object.assign(flowstep.options, {
      query: this.query,
      showLabels: this.options.showLabels,
    });

    console.log('SOSL::_saveFlowstep_: ', flowstep);
    return flowstep;
  }

  async _tryLoadFlowstepOfDifferentType_(allWithSameTarget, allSimilarTypesWithSameTarget) {
    const allInserts = allWithSameTarget.filter(x => isInsertKind(x.type));
    if (allInserts.length) {
      this.selectedObjectName = allInserts[0].options.mapping.sfObjectName;
    }
  }

  _renderBody_(renderProps) {
    const { getStepTitle } = renderProps;
    const { CodeMirror } = this.soql;

    // TODO: styles
    return (
      <>
        <div className={classNames.optionsContentV}>
          {this.renderCreateNewSheet(renderProps)}
        </div>

        <div className={classNames.optionsContentV}>
          <Step title={getStepTitle('Enter text to search')} >
            <div className={classNames.withTargetPicker}>
              < TextField
                placeholder={'Enter the value'} 
                styles={{ root: { width: '100%' } }} 
                value={this.findExpression} 
                onChange={(event, value) => this.handleSearchExpressionChanged(value)} 
              />
              <TargetPicker selectionOnly={true} showRange={false} onChange={this.handleSearchExprssionTargetPicked} />
            </div>
            </Step>

            <Step title={getStepTitle('Select which types of text fields to search in')} >
              <SearchableComboBox
                placeholder={'Select IN type'}
                selectedKey={this.inExpression}
                onChange={(event, item) => this.handleInTypeChanged(item.key)}
                options={this.inTypeOptions}
                // disabled={this._readOnly_}
              />
            </Step>

          <Step title={getStepTitle('Select the returning object')} >
            <SearchableComboBox
              placeholder="Select object"
              selectedKey={this.selectedObjectName}
              onChange={async (event, item) => this.handleSelectObject(item.key)}
              options={this.objects}
              disabled={!this.objects.length || this._readOnly_}
            />
            <Checkbox label="Show labels"
              disabled={this._readOnly_}
              checked={this.options.showLabels}
              onChange={(_, checked) => this.handleOptionsChange('showLabels', checked)}
            />
          </Step>

            <Step title={getStepTitle('Select fields to return')} >
              <CheckboxTreeListWithSearch
                searchPlaceholder={'Start typing to filter'}
                items={this.fields}
                selectedIds={this.selectedFieldsIds}
                expandedIds={this.expandedFieldsIds}
                filter={this.fieldsFilter}
                disabled={this._readOnly_}
                onItemsSelected={this.handleFieldsSelected}
                onItemsCollapsed={this.handleFieldsCollapsed}
                onFilterChanged={this.handleFieldsFilterChanged}
                onFilterApplied={this.handleFieldsFilterApplied}
                onRenderItemTitle={this.handleRenderFieldTitle}
              />
            </Step>

          </div>
        <GroupPane className={classNames.options} title='Edit Query' collapsed={true}>
          {CodeMirror &&
            <CodeMirror
              className={classNames.codemirror}
              value={this.query}
              options={{
                mode: 'text/x-sql',
                theme: 'default',
                lineNumbers: false,
                lineWrapping: true,
                placeholder: 'Enter a valid SOSL query text here or build one using the Query Builder controls above',
                // readOnly: true,
              }}
              onBeforeChange={(editor, data, value) => {
                this.query = value;
                this.setDirty();
              }}
            />
          }
        </GroupPane>

        {
        this.fakeSubstitutedQuery.hasSubstitutions &&
        <GroupPane className={classNames.options} title='Query Preview'>
          <div className={classNames.block}>
            <div className={classNames.buttonBlock}>
              <DefaultButton text='Calculate query' onClick={this.handlePreviewQuery} primary={true}
                disabled={!this.isValidQuery}
              />
            </div>
          </div>

          { CodeMirror &&
            <CodeMirror
            className={classNames.codemirror}
            value={this.queryPreview}
            options={{
              mode: 'text/x-sql',
              theme: 'default',
              lineNumbers: false,
              lineWrapping: true,
              placeholder: 'Query preview will be displayed here',
              readOnly: this._readOnly_,
            }}
            onBeforeChange={(editor, data, value) => {
              this.queryPreview = value;
            }}
            />
          }
        </GroupPane>
        }

        <div className={classNames.contentWithoutGroupPane}>
          {/* { this.renderErrorReaction(renderProps, { name: 'SOSL query', action: 'refreshed' }) } */}
          { this.renderPullModeCombobox(renderProps) }
        </div>

      </>
    );
  }

  @action handleSearchExpressionChanged = (value) => {
    this.findExpression = value;
    this.buildQuery();
  }

  @action handleSearchExprssionTargetPicked = (target) => {
    this.findExpression = QS.toSubstitution({ address: target.addresses[0].address });
    this.buildQuery();
  }

  @action handleInTypeChanged = (type) => {
    this.inExpression = type;
    this.buildQuery();
  }

  @action handleSelectObject(objectName) {
    runShortAction(async () => {
      this.selectedFieldsIds = [];
      this.expandedFieldsIds = [];
      this.fieldsFilter = '';
      this.selectedObjectName = objectName;
      this.sourceFields = await this.loadRelationship({ referencesTo: [objectName] });

      this.buildQuery();
    });
  }

  @action handleFieldsSelected = (areSelected, items, allSelectedItems) => {
    this.selectedFieldsIds = allSelectedItems;
    this.buildQuery();
  }

  @action handleFieldsCollapsed = async (areCollapsed, items, itemsToLoad, allExpandedItems) => {
    this.expandedFieldsIds = [...allExpandedItems, ...(await this.loadFields(itemsToLoad))];
  }

  @action handleFieldsFilterChanged = (filter) => {
    this.fieldsFilter = filter;
  }

  async fetchObjects() {
    await this.props.officeStore.fetchObjects();
  }

  buildQuery = () => {
    let query = '';

    if (!this.findExpression || !this.selectedObjectName) {
      this.query = '';
      this.setDirty();
      return;
    }

    query += `FIND {${this.findExpression}}`

    const inTypeHelper = {
      [QA.InFieldsType.All]: 'ALL FIELDS',
      [QA.InFieldsType.Email]: 'EMAIL FIELDS',
      [QA.InFieldsType.Name]: 'NAME FIELDS',
      [QA.InFieldsType.Phone]: 'PHONE FIELDS',
      [QA.InFieldsType.Sidebar]: 'SIDEBAR FIELDS',
    };

    query += ` IN ${inTypeHelper[this.inExpression]}`
    const fieldNames = getItems(this.sourceFields, this.selectedFieldsIds).map(x => x.fullName).join(', ');
    const fieldExpr = fieldNames ? `(${fieldNames})` : '';
    query += ` RETURNING ${this.selectedObjectName}${fieldExpr}`

    this.query = query;
    this.setDirty();
  }

  @action loadFields = async (idsToLoad) => {
    if (idsToLoad.length) {
      updateItems(this.sourceFields, idsToLoad.map(id => ({ id, isLoading: true })));

      const loaded = await Promise.all(idsToLoad.map(async (id) => {
        const descendants = await this.loadRelationship(getItem(this.sourceFields, id));
        return { descendants, id };
      }));

      updateItems(this.sourceFields, loaded.map(x => ({ id: x.id, isLoading: false, descendants: x.descendants })));
    }
    return idsToLoad;
  }

  handleFieldsFilterApplied = (field, filter) => {
    return includesParttern(this.options.showLabels ? field.label : field.name, filter);
  }

  toSourceFields = (rawFields, parent) => {
    // console.log('Soql::toSourceFields: ', parent, rawFields);

    const nestingDepth = (parent.nestingDepth ?? 0) + 1;
    const parentRelationshipNames = parent.parentRelationshipNames
      ? [...parent.parentRelationshipNames, parent.rawField.relationshipName]
      : [];

    return rawFields.map((rawField, index) => {
      const id = makeId(index, parent.id);
      const { name, label } = rawField;
      const fullName = QA.toFullName(parentRelationshipNames, name);
      const fullLabel = QA.toFullLabel(parentRelationshipNames, label);
      const canHaveDescendants = nestingDepth < Data.MaxRelationshipDepth && QA.isRelationshipField(rawField);
      const isPolymorphic = QA.isPolymorphicField(rawField);

      const baseObject = {
        isLoading: false, parentRelationshipNames, name, label,
        fullName, fullLabel, rawField, nestingDepth,
      };

      const descendants = isPolymorphic && canHaveDescendants
        ? rawField.referenceTo.map((referenceTo, referenceIndex) => ({
          ...baseObject,
          id: makeId(referenceIndex, id),
          isSelectable: false,
          canHaveDescendants: true,
          descendants: [],
          referencesTo: [referenceTo],
          isPolymorphicChild: true,
        }))
        : [];

      return {
        ...baseObject,
        id,
        isPolymorphic,
        isSelectable: true,
        canHaveDescendants,
        descendants,
        referencesTo: canHaveDescendants ? rawField.referenceTo : [],
      };
    });
  }

  @action handlePreviewQuery = async () => {
    await runShortAction(async () => {
      this.queryPreview = await this.props.officeStore.substituteQueryOnActiveWorksheet(this.query, this.type);
    });
  }


  fetchObjectData = async (objectName) => {
    return this.props.officeStore.fetchObjectData({ objectName });
  }

  parseQuery = async (query) => {
    try {
      return await this.props.salesforceStore.parseSoslQuery(query);
    }
    catch (err) {
      return { columns: [], objectName: '', isValid: false };
    }
  }

  extractQueryData(query) {
    return QA.extractSoslQueryData(query);
  }

  parseAndLoadQuery = async (query) => {
    const { columns, objectName, ...rest } = await this.parseQuery(query);
    const root = { descendants: [] };

    const loadFieldTree = (relationships) => {
      let parent = root;
      for (const relationship of relationships) {
        const { fieldIndex, rawFields, type } = relationship;
        if (!parent.descendants.length) {
          parent.descendants = this.toSourceFields(rawFields, parent);
        }

        const field = parent.descendants[fieldIndex];
        if (type === QA.RelationshipType.Field) {
          return field;
        }

        parent = field;
        if (field.isPolymorphic) {
          parent = parent.descendants.find(x => x.referencesTo[0] === relationship.referenceTo);
        }
      }
    }

    const usedFields = columns.map(({ relationships }) => loadFieldTree(relationships).id);

    return { objectName, sourceFields: root.descendants, usedFields, ...rest };
  }

  @action loadRelationship = async (parent) => {
    const { fields } = await this.fetchObjectData(parent.referencesTo[0]);
    return this.toSourceFields(fields, parent);
  }

  handleRenderFieldTitle = (item, nestingDepth) => {
    const { originalItem, isActive } = item;
    const { title, tooltip } = originalItem;

    return (
      <ButtonTooltip content={tooltip} styles={{ root: { width: 'auto' } }} >
        <span className={classes(classNames.fieldText, { [classNames.fieldTextInactive]: !isActive })}>{title}</span>
      </ButtonTooltip>
    );
  }
}
